import { useEffect, useRef, useState } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;
function loadScript() {
    if (typeof window === "undefined") return Promise.resolve();
    if (window.turnstile) return Promise.resolve();
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = reject;
        document.head.appendChild(s);
    });
    return scriptPromise;
}

/**
 * Cloudflare Turnstile widget.
 * If REACT_APP_TURNSTILE_SITE_KEY is not set, renders nothing and onToken is never called —
 * which means form submissions go through without a token (backend will also skip verification).
 * Once both site_key + secret are configured, spam protection activates automatically.
 */
export default function TurnstileWidget({ onToken, theme = "light", size = "normal" }) {
    const ref = useRef(null);
    const [, setError] = useState(null);

    useEffect(() => {
        if (!SITE_KEY) return;
        let widgetId;
        let cancelled = false;
        loadScript()
            .then(() => {
                if (cancelled || !ref.current || !window.turnstile) return;
                widgetId = window.turnstile.render(ref.current, {
                    sitekey: SITE_KEY,
                    theme,
                    size,
                    callback: (token) => onToken && onToken(token),
                    "error-callback": () => onToken && onToken(""),
                    "expired-callback": () => onToken && onToken(""),
                });
            })
            .catch((e) => setError(e));
        return () => {
            cancelled = true;
            if (widgetId && window.turnstile) {
                try { window.turnstile.remove(widgetId); } catch (e) { /* noop */ }
            }
        };
    }, [theme, size, onToken]);

    if (!SITE_KEY) return null;
    return <div data-testid="turnstile-widget" ref={ref} className="my-2" />;
}
